import { useEffect, useRef } from "react";

import { useTransactionsState } from "@bridgesplit/react";
import { useTransactionWalletAuth } from "@bridgesplit/abf-react";
import { DialogWrapper, DialogWrapperProps } from "@bridgesplit/ui";
import { AppDialog, useAppDialog } from "app/utils";

import WalletValidatorWrapper from "./WalletValidatorWrapper";
import useMinTransactionBalance from "./useMinTransactionBalance";

/**
 * Transaction dialog is managed separately from other dialogs to allow for
 * transaction sender to be open while other dialogs are open
 */
export default function TransactionsDialog() {
    const { reset } = useTransactionsState();

    const getAuth = useTransactionWalletAuth();

    const { balanceCheckNeeded, BalancePrompt } = useMinTransactionBalance();

    const { getData, close } = useAppDialog();
    const multiPartSender = getData(AppDialog.TransactionSender);
    function reject() {
        close();
        reset();
        multiPartSender?.reject?.();
    }

    const dialogProps: Partial<DialogWrapperProps> = {
        onClose: reject
    };

    if (!multiPartSender) return null;

    if (balanceCheckNeeded) {
        return (
            <DialogWrapper {...dialogProps}>
                <BalancePrompt />
            </DialogWrapper>
        );
    }

    // Resolves the transaction promise as soon as this is returned hit
    return (
        <WalletValidatorWrapper
            dialog={{
                description: multiPartSender.description,
                props: dialogProps
            }}
        >
            <AsyncSuccess
                resolve={() => {
                    close();
                    const auth = getAuth();
                    if (auth.isOk()) {
                        multiPartSender.resolve?.(auth.unwrap());
                    } else {
                        multiPartSender.reject?.();
                    }
                }}
            />
        </WalletValidatorWrapper>
    );
}

function AsyncSuccess({ resolve }: { resolve: () => void }) {
    // use ref to exactly call once even in strict dev mode
    const queryStarted = useRef<boolean>();

    useEffect(() => {
        if (queryStarted.current) return;
        queryStarted.current = true;
        resolve();
    }, [resolve]);

    return null;
}
